import { Button } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
    Page,
    PermissionKeys,
    RowActions,
    Search,
    SortOrder,
    Table,
    useHasPermission,
    useQueryParams,
} from '../../../shared';
import { DownloadCard } from '../../components';
import { useDeleteStockLocation, useStockLocationsList } from '../../hooks';

export const StockLocationsPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.STOCK_LOCATIONS_WRITE);

    const { page, setPage, pageSize, setPageSize, sort, setSort, search, setSearch, debouncedSearch } =
        useQueryParams();

    const { isPending, data } = useStockLocationsList({
        page: page,
        pageSize: pageSize,
        sortBy: sort[0].field,
        sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
        search: debouncedSearch,
    });

    const { mutate: deleteStockLocation } = useDeleteStockLocation();

    const columns: GridColDef[] = [
        { field: 'name', headerName: t('name'), minWidth: 150, flex: 1 },
        { field: 'code', headerName: t('code'), minWidth: 150, flex: 1 },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 140,
            align: 'right',
            sortable: false,
            renderCell: ({ row: { id, code, name } }: GridCellParams) => (
                <>
                    <DownloadCard useIcon={true} code={code} name={name} />
                    {hasWritePermission && (
                        <RowActions
                            onEdit={() => navigate(`/admin/stock-locations/${id}/edit`)}
                            onDelete={() => deleteStockLocation(id)}
                            deleteWarningTitle={t('stockLocationDeleteWarningTitle')}
                            deleteWarningText={t('stockLocationDeleteWarningText')}
                        />
                    )}
                </>
            ),
        },
    ];

    return (
        <Page
            title={t('stockLocations')}
            actions={[
                <Search search={search} onSearch={setSearch} />,
                hasWritePermission && (
                    <Button
                        component={Link}
                        to="/admin/stock-locations/new"
                        color="primary"
                        variant="contained"
                        data-testid="new-stockLocation"
                    >
                        {t('newStockLocation')}
                    </Button>
                ),
            ]}
        >
            <Table
                rows={data?.data || []}
                columns={columns}
                rowCount={data?.pagination?.size || 0}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
                loading={isPending}
                sortModel={sort}
                onSortModelChange={setSort}
                onRowClick={(row) => hasWritePermission && navigate(`/admin/stock-locations/${row.id}/edit`)}
            />
        </Page>
    );
};
